import { Col, DatePicker, Form, Input, InputNumber, Row, Switch } from 'antd';
import type { FindAssetCustomFieldResponseDto } from '@evimo/types/api/asset-custom-field/find-asset-custom-field/find-asset-custom-field.response.dto';
import { AssetCustomFieldType } from '@evimo/types/enums/asset-custom-field-type';
import { useTranslation } from 'react-i18next';
import { getUnitFromRules } from '~/app/components/asset-custom-fields/utils';

type Props = {
  fields: FindAssetCustomFieldResponseDto[];
  /** Nest form path prefix, e.g. "customFields" → name={[prefix, fieldId]} */
  namePrefix?: string;
  disabled?: boolean;
};

export function AssetCustomFieldsFormSection({
  fields,
  namePrefix = 'customFields',
  disabled = false,
}: Props) {
  const { t } = useTranslation(['assets', 'asset-custom-fields']);

  if (fields.length === 0) {
    return null;
  }

  return (
    <Row gutter={[16, 8]}>
      {fields.map((field) => {
        const unit = getUnitFromRules(field.rules);
        const label = unit ? `${field.name} (${unit})` : field.name;

        return (
          <Col xs={24} md={12} key={field.id}>
            <Form.Item
              name={[namePrefix, field.id]}
              label={label}
              rules={
                field.required
                  ? [
                      {
                        required: true,
                        message: t('detail.customFields.required', {
                          defaultValue: '"{{name}}" is required',
                          name: field.name,
                        }),
                      },
                    ]
                  : undefined
              }
              valuePropName={
                field.fieldType === AssetCustomFieldType.BOOLEAN
                  ? 'checked'
                  : 'value'
              }
            >
              {renderControl(field, disabled, t)}
            </Form.Item>
          </Col>
        );
      })}
    </Row>
  );
}

function renderControl(
  field: FindAssetCustomFieldResponseDto,
  disabled: boolean,
  t: (key: string, options?: { defaultValue?: string }) => string,
) {
  switch (field.fieldType) {
    case AssetCustomFieldType.NUMBER:
      return (
        <InputNumber
          style={{ width: '100%' }}
          disabled={disabled}
          placeholder={field.fieldKey}
        />
      );
    case AssetCustomFieldType.DATE:
      return (
        <DatePicker
          style={{ width: '100%' }}
          disabled={disabled}
          showTime
          placeholder={t('detail.customFields.datePlaceholder', {
            defaultValue: 'Select date',
          })}
        />
      );
    case AssetCustomFieldType.BOOLEAN:
      return <Switch disabled={disabled} />;
    case AssetCustomFieldType.TEXT:
    default:
      return <Input disabled={disabled} placeholder={field.fieldKey} />;
  }
}
